import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./Faq.css";

type FaqEntry = { question: string; answer: string | string[] };

type ServiceKey =
  | "nbn"
  | "sip-business"
  | "voice-pooling"
  | "data-pooling"
  | "hosted-voice";

type Service = {
  key: ServiceKey;
  label: string;
  description: string;
  faqs: FaqEntry[];
};

const services: Service[] = [
  {
    key: "nbn",
    label: "NBN (Personal & Business)",
    description: "Home and business NBN setup, relocations, fees, and faults.",
    faqs: [
      {
        question: "What happens if I need to relocate my NBN service?",
        answer: "Check if service is available at the new address, submit a relocation request, and note that fees can vary by connection type.",
      },
      {
        question: "What should I do if my NBN service is not working?",
        answer: "Restart your modem/router, check for outages in your area, then contact Jortel support. An NBN tech may be required if the issue persists.",
      },
      {
        question: "What is the difference between FTTP, FTTN, and FTTC?",
        answer: [
          "FTTP: Fibre direct to your premises; fastest and most reliable.",
          "FTTN: Fibre to a nearby node, copper the last stretch; slower.",
          "FTTC: Fibre to the curb, short copper run; mid-tier speeds.",
          "HFC: Uses pay-TV coax; good speeds but can vary with congestion.",
        ],
      },
      {
        question: "How can I upgrade my connection to FTTP?",
        answer: [
          "From FTTN: order at least 100/20 Mbps.",
          "From FTTC: order at least 250/25 Mbps.",
          "Downgrading below these speeds or cancelling within 12 months can trigger a 65% ETC.",
        ],
      },
      {
        question: "Are there any additional installation or service fees?",
        answer: [
          "New Development Charge: $389.99",
          "Service Activation: $99",
          "Professional Installation (if required): $299",
          "Non-standard NBN Co/Opticomm work: As billed by NBN Co/Opticomm",
        ],
      },
      {
        question: "What happens if an NBN technician is called to my premises by mistake?",
        answer: [
          "Incorrect call-out (technician attends): As billed by NBN Co/Opticomm",
          "Incorrect call-out (no attendance): As billed by NBN Co/Opticomm",
          "Late cancellation of an appointment: As billed by NBN Co/Opticomm",
        ],
      },
      {
        question: "What happens if I need to modify or repair my connection?",
        answer: "Modifications, removals, or repairs of connecting equipment are billed per NBN Co/Opticomm charges and require approval before work.",
      },
      {
        question: "What if I want to cancel my Jortel NBN plan early?",
        answer: "A 65% Early Termination Charge (ETC) applies on the remaining contract value.",
      },
      {
        question: "How long does it take to get my NBN service activated?",
        answer: "Activation is immediate once your modem is connected and provisioned. If a technician visit is required, timing depends on their availability.",
      },
      {
        question: "What is a Static IP?",
        answer: "A fixed, unchanging IP address that supports hosting, remote access, and secure connections without IP changes.",
      },
    ],
  },
  {
    key: "sip-business",
    label: "SIP Trunking (Business)",
    description: "Call rates, provisioning, PBX integration and setup.",
    faqs: [
      {
        question: "What is SIP Trunking and how does it work?",
        answer: "Calls run over the internet instead of copper lines, linking your PBX to the public network via broadband for scalable, cost-effective voice.",
      },
      {
        question: "How many calls can I make at once?",
        answer: "Each SIP channel supports one active call. Example: 10 channels = 10 concurrent calls. Add/remove channels anytime.",
      },
      {
        question: "What is the difference between PAYG and Premium?",
        answer: [
          "PAYG: Lower monthly channel cost; pay per minute for calls.",
          "Premium: Higher monthly channel cost; local, national, mobile and 1800 calls included (13/1300 excluded).",
        ],
      },
      {
        question: "Are there any long-term contracts?",
        answer: "No. SIP services are month-to-month so you can scale without penalties.",
      },
      {
        question: "Can I keep my existing phone numbers?",
        answer: "Yes. Most numbers can be ported to Jortel with minimal disruption.",
      },
      {
        question: "Is this service compatible with my current phone system?",
        answer: "Works with SIP-compatible PBXs. If unsure, Jortel can confirm and assist with setup.",
      },
      {
        question: "How reliable is the service?",
        answer: "Runs on high-availability, redundant infrastructure designed for clear calls and strong uptime.",
      },
      {
        question: "How quickly can I get started?",
        answer: "After provisioning, trunks are typically active within 1-2 business days. Setup support is available.",
      },
    ],
  },
  {
    key: "voice-pooling",
    label: "Mobile / Voice Plans",
    description: "SIM-only, coverage, roaming, and keeping numbers.",
    faqs: [
      { question: "What is a SIM-only plan?", answer: "Mobile service without a new handset; use your existing device." },
      { question: "What coverage can I expect?", answer: "The mobile product of Jortel uses the Telstra Wholesale Mobile Network, with predicted coverage areas in all Australian major cities, most outer-metro areas and larger regional centres, as well as many smaller towns and transport corridors. Predicted coverage details are available on the coverage map page." },
      {
        question: "What is data pooling and how does it work?",
        answer: "Share one pool of data across users (up to 300 per pool) for efficient usage.",
      },
      { question: "Can I make international calls from Australia?", answer: "Some plans include unlimited international calls and SMS to selected countries; check your plan." },
      { question: "Can I use international roaming?", answer: "Available on select plans; add roaming packs for overseas coverage." },
      { question: "Can I upgrade my plan mid-month?", answer: "Yes. Previous inclusions may not carry over, so align changes with your billing cycle when possible." },
      { question: "Can I keep my existing phone number?", answer: "Yes, you can port your current number when switching." },
      { question: "How much data do I need on my mobile plan?", answer: "Light use ~10GB; heavy streamers or business use may need 60GB+." },
      { question: "What is an eSIM and can I get one?", answer: "A digital SIM you activate without a physical card; supported on compatible devices." },
    ],
  },
  {
    key: "data-pooling",
    label: "Data Pooling & Banking",
    description: "How banking, pooling, and limits work across users.",
    faqs: [
      { question: "What is J Mobile Data Banking?", answer: "Store unused monthly data (up to 500GB) for future use on eligible plans." },
      { question: "How does Data Banking work?", answer: "Unused data each month is added automatically while your plan supports banking and remains active." },
      { question: "What happens if I downgrade my plan?", answer: "Banked data is forfeited when downgrading to a smaller data plan." },
      { question: "Will my data bank be affected if my service is suspended?", answer: "Banked data stays, but you do not accumulate more during suspension." },
      { question: "What happens if I switch to a Data Pooling plan?", answer: "Previously banked data is forfeited; pooling does not support banking." },
      { question: "What is Data Pooling?", answer: "Multiple users share one combined pool of data across enrolled plans." },
      { question: "How many users can be in a Data Pool?", answer: "Up to 300 users per pool; create additional pools if needed." },
      { question: "Does Data Pooling support Data Banking?", answer: "No. Unused pooled data expires at the end of the billing cycle." },
      { question: "Can I mix different plans in a Data Pool?", answer: "Yes. 4G, 5G, mobile, and data-only plans can share the same pool." },
      { question: "Can I use Data Pooling for international roaming?", answer: "No. Pooling is for domestic data only and excludes roaming and voice/SMS/MMS." },
    ],
  },
  {
    key: "hosted-voice",
    label: "Hosted Voice (Business)",
    description: "Licensing, inclusions, hardware, and reliability.",
    faqs: [
      {
        question: "What is Jortel Communications' Hosted Voice service?",
        answer: "A cloud-based phone system with advanced call management, scalability, and no lock-in contracts.",
      },
      {
        question: "What is the difference between the PAYG and Premium Plans?",
        answer: [
          "PAYG: $20/license/month, pay-as-you-go calling; good for variable call volumes.",
          "Premium: $45/license/month, includes unlimited AU local, national, and mobile calls; excludes 13/1300.",
        ],
      },
      { question: "Do I need special equipment to use Hosted Voice?", answer: "No extra hardware if you use a softphone. Compatible IP desk phones are optional." },
      { question: "Can I keep my existing phone number?", answer: "Yes, you can port existing business numbers with minimal downtime." },
      { question: "How long does it take to set up Hosted Voice?", answer: "Typically a few business days. Number porting may add time depending on your current provider." },
      { question: "What happens if my internet goes down?", answer: "Calls can be forwarded to mobiles or alternate numbers to keep you reachable." },
      { question: "Are international calls included?", answer: "Not in base plans. Competitive international rates are available separately." },
      { question: "Is there a contract or minimum commitment?", answer: "No. Plans are month-to-month for flexible scaling." },
      {
        question: "What business features are included?",
        answer: "Call recording, auto-attendant, music on hold, call queuing, hunt groups, and optional UC bolt-ons for voice, video, and messaging.",
      },
      { question: "Can I add or remove users as my business grows?", answer: "Yes. Add or remove licenses anytime without long-term lock-in." },
    ],
  },
];

export default function Faq(): JSX.Element {
  const [activeKey, setActiveKey] = useState<ServiceKey | null>(null);
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const activeService = services.find((s) => s.key === activeKey);

  const toggleQuestion = (question: string) => {
    setOpenQuestion((prev) => (prev === question ? null : question));
  };

  const renderAnswer = (answer: string | string[]) => {
    if (Array.isArray(answer)) {
      return (
        <ul className="faq-answer-list">
          {answer.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    }
    return <p className="faq-answer-text">{answer}</p>;
  };

  return (
    <motion.main className="page faq-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <header className="faq-hero">
        <div>
          <p className="eyebrow">Help Articles &amp; FAQ</p>
          <h1>Get quick answers for your service</h1>
          <p className="lede">Choose your product and browse its FAQ in one place. No downloads or pop-ups.</p>
        </div>
        <a className="btn primary" href="/contact">Back to Contact</a>
      </header>

      <section className="faq-layout">
        <div className="faq-selector">
          <h2 className="selector-title">Select a service</h2>
          <div className="faq-chip-row">
            {services.map((service) => (
              <button
                key={service.key}
                className={`faq-chip ${service.key === activeKey ? "active" : ""}`}
                onClick={() => {
                  setActiveKey(service.key);
                  setOpenQuestion(null);
                }}
              >
                <span className="faq-chip-title">{service.label}</span>
                <span className="faq-chip-desc">{service.description}</span>
              </button>
            ))}
          </div>
        </div>

        {activeService ? (
          <div className="faq-panel">
            <div className="faq-panel-header">
              <div>
                <p className="eyebrow small">Viewing</p>
                <h3>{activeService.label}</h3>
              </div>
              <div className="faq-panel-note">Content taken from the latest published FAQ for this service.</div>
            </div>

            <div className="faq-accordion">
              <AnimatePresence initial={false}>
                {activeService.faqs.map((faq) => {
                  const open = openQuestion === faq.question;
                  return (
                    <motion.div
                      key={faq.question}
                      className={`faq-item ${open ? "open" : ""}`}
                      initial={false}
                      animate={{ backgroundColor: open ? "#f3f6ff" : "#ffffff" }}
                      transition={{ duration: 0.2 }}
                    >
                      <button className="faq-question" onClick={() => toggleQuestion(faq.question)}>
                        <span>{faq.question}</span>
                        <span className={`chevron ${open ? "rotated" : ""}`}>&gt;</span>
                      </button>
                      <AnimatePresence initial={false}>
                        {open && (
                          <motion.div
                            className="faq-answer"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            {renderAnswer(faq.answer)}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
        ) : null}
      </section>
    </motion.main>
  );
}
